import { motion } from "framer-motion";
import { Leaf, Waves, Users, HeartHandshake } from "lucide-react";
import { IMAGES } from "../data/content";

const PILLARS = [
    { icon: Leaf, title: "Grown in Nature", text: "Trees breathe life here, herbs grow fresh, and every corner is wrapped in green.", color: "#00958C", testid: "pillar-nature" },
    { icon: Waves, title: "Shanti & Tehrav", text: "Still waters, calm corners. A pause worth taking, a place to slow down.", color: "#1C8DB9", testid: "pillar-calm" },
    { icon: Users, title: "One Shared Table", text: "Families, friends, pets, generations, everyone belongs at this table.", color: "#F0841B", testid: "pillar-together" },
    { icon: HeartHandshake, title: "Rooted in Flavour", text: "Three kitchens, infinite tastes, and moments that nourish beyond the plate.", color: "#E21B54", testid: "pillar-flavour" },
];

export const Story = () => {
    return (
        <section id="story" className="relative py-24 md:py-36 px-6 md:px-10" data-testid="story-section">
            <div className="max-w-7xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7 }}
                    >
                        <p className="font-serif-brand italic text-xl text-[#F0841B] mb-4">A retreat, not just a restaurant</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-[#262055] leading-[1.05]" data-testid="story-headline">
                            More than food.<br />
                            <span className="text-[#00958C]">Uncover life.</span>
                        </h2>
                        <p className="mt-8 text-base md:text-lg leading-relaxed text-[#262055]/75 max-w-xl" data-testid="story-body">
                            Uncover Avenue is where warmth wins over walls, and colour wins over
                            convention. Surrounded by trees and calm corners, it is a place for
                            laughter, long conversations, and quiet moments that matter, from
                            pottery afternoons to sunset meditations.
                        </p>
                        <p className="mt-6 font-serif-brand italic text-lg text-[#262055]/70" data-testid="story-hindi-line">
                            Come as you are, and leave with a refreshed heart.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <img
                            src={IMAGES.pavilionTrees}
                            alt="Uncover Avenue pavilion seen through the trees"
                            className="rounded-[2.5rem] w-full aspect-[4/3] object-cover shadow-2xl shadow-[#262055]/20"
                            data-testid="story-image"
                        />
                        <div className="absolute -bottom-8 -left-4 md:-left-8 bg-white rounded-3xl px-8 py-6 shadow-xl shadow-[#262055]/15 float-slow" data-testid="story-quote-card">
                            <p className="font-serif-brand italic text-lg md:text-xl text-[#262055]">"Uncover more than food.<br />Uncover life."</p>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-32 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pillars-grid">
                    {PILLARS.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="bg-white rounded-3xl p-8 shadow-lg shadow-[#262055]/5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                            data-testid={p.testid}
                        >
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${p.color}1A` }}>
                                <p.icon size={22} style={{ color: p.color }} />
                            </div>
                            <h3 className="text-lg font-semibold text-[#262055] mb-2">{p.title}</h3>
                            <p className="text-sm leading-relaxed text-[#262055]/65">{p.text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
